import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

// Mirrors WRITE_ROLES in the backend. Viewer is deliberately absent: it is the
// read-only role, and every control it was shown ended in a 403 toast.
const WRITE_ROLES = ["Admin", "HR Manager", "Department Head", "Unit Coordinator", "HR"];
export const canWrite = (user) => !!user && WRITE_ROLES.includes(user.role);

// Mirrors require_admin. The audit log and the user list are the two surfaces where the
// API refuses everybody else, so the nav must not offer them.
export const isAdmin = (user) => !!user && user.role === "Admin";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // "we could not ask" is a different answer from "you are not signed in". Treating
  // every failure as the latter meant one 500, or a moment without a network, threw
  // the user out to a login page that could not reach the server either.
  const [unreachable, setUnreachable] = useState(false);

  const probe = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setUnreachable(false);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        setUser(false);            // a real answer: not signed in
        // Drop the dead token too. The response interceptor skips /auth/ URLs, so an
        // expired session left its token in storage to be sent with every later request.
        localStorage.removeItem("token");
        setUnreachable(false);
      } else {
        setUnreachable(true);      // no answer: server down, 5xx, blocked, offline
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Skip auth probe on public routes (verify page has no auth requirement)
    if (window.location.pathname.startsWith("/verify")) {
      setUser(false);
      setLoading(false);
      return;
    }
    probe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUnreachable(false);
    localStorage.setItem("token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, unreachable, retry: probe, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
